/**
 * DEV-026 · Shared To-do workspace for Field Resource and Sales.
 *
 * Presents ad-hoc tasks assigned to (or created by) the caller. A
 * to-do IS an ad-hoc task — the workspace title differs but the row
 * is one and the same. Formal workflow tasks remain in Tasks; only
 * `kind === "ad_hoc"` rows are shown here.
 *
 * The "+ New to-do" affordance opens the shared NewTodoModal which
 * calls `POST /tasks` with an optional `project_id`. The backend
 * defaults personal items to the current user (see DEV-026 changes
 * in `routers/tasks.py::create_task`).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ListTodo, Loader2, Plus } from "lucide-react";

import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { api, formatApiError } from "@/lib/api";
import NewTodoModal from "@/components/todo/NewTodoModal";

export default function TodoWorkspace({
    eyebrow = "TO-DO",
    subtitle = "Your ad-hoc to-do list.",
    testidPrefix = "todo-workspace",
    /** Optional additional rows (e.g. Sales Actions) rendered under a
     *  clearly labelled sub-section so they don't blend with ad-hoc. */
    additionalSection = null,
}) {
    const [filter, setFilter] = useState("outstanding");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [completingId, setCompletingId] = useState(null);
    // DEV-026 · Authorised project options for the shared New to-do
    // modal. Populated via the same `taskCreationOptions` endpoint
    // used by InstallerTasks — server returns only projects the
    // caller may create tasks on, so the selector never leaks an
    // unauthorised project. Empty → modal renders as personal-only.
    const [projectOptions, setProjectOptions] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const rows = await api.taskCreationOptions();
                if (cancelled) return;
                const mapped = (rows || []).map((p) => ({
                    id: p.id,
                    label: [p.customer_name, p.address].filter(Boolean).join(" · "),
                }));
                setProjectOptions(mapped);
            } catch { if (!cancelled) setProjectOptions([]); }
        })();
        return () => { cancelled = true; };
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Reuses `GET /tasks`. Backend already applies DEV-026
            // visibility so we only receive rows the caller can see.
            const scope = filter === "completed" ? "completed" : "pending";
            const r = await api.listTasks({ scope });
            const adHoc = (Array.isArray(r) ? r : []).filter(
                (t) => (t.kind || "ad_hoc") === "ad_hoc",
            );
            setRows(adHoc);
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || "Failed to load to-do list.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const onCreated = async () => {
        setModalOpen(false);
        toast.success("To-do created.");
        await load();
    };

    const onComplete = async (row) => {
        setCompletingId(row.id);
        try {
            await api.completeTask(row.id);
            toast.success("To-do completed.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to complete.");
        } finally {
            setCompletingId(null);
        }
    };

    const total = useMemo(() => rows.length, [rows]);

    return (
        <PageContainer width="7xl" testid={`${testidPrefix}-page`}>
            <PageHeader
                eyebrow={eyebrow}
                title="To-do"
                subtitle={subtitle}
                icon={ListTodo}
                testid={`${testidPrefix}-header`}
                rightSlot={(
                    <button
                        type="button"
                        onClick={() => setModalOpen(true)}
                        data-testid={`${testidPrefix}-new-btn`}
                        className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-md bg-[#131B2F] text-white text-sm font-medium hover:bg-[#0F1526]"
                    >
                        <Plus size={14} strokeWidth={1.75} /> New to-do
                    </button>
                )}
            />

            {/* Filter row */}
            <div className="flex items-center gap-1.5 mb-4" data-testid={`${testidPrefix}-filters`}>
                <div className="inline-flex items-center bg-white border border-[#E5E7EB] rounded-md p-0.5">
                    {[
                        { key: "outstanding", label: "Outstanding" },
                        { key: "completed",   label: "Completed" },
                    ].map((f) => (
                        <button
                            key={f.key}
                            type="button"
                            onClick={() => setFilter(f.key)}
                            data-testid={`${testidPrefix}-filter-${f.key}`}
                            aria-pressed={filter === f.key}
                            className={`px-3 py-1.5 rounded text-sm font-medium ${filter === f.key ? "bg-[#131B2F] text-white" : "text-[#131B2F] hover:bg-[#F5F5F7]"}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="ml-auto text-xs text-[#4B5565]" data-testid={`${testidPrefix}-count`}>
                    {loading ? "" : `${total} ${total === 1 ? "item" : "items"}`}
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-[#4B5565]" data-testid={`${testidPrefix}-loading`}>Loading…</div>
            ) : error ? (
                <div className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-md px-3 py-2" data-testid={`${testidPrefix}-error`}>
                    {error}
                </div>
            ) : rows.length === 0 ? (
                <div className="bg-white rounded-xl border border-[#E5E7EB] p-10 text-center" data-testid={`${testidPrefix}-empty`}>
                    <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-[#F1F5F9] text-[#475569] mb-3">
                        <ListTodo size={20} strokeWidth={1.75} />
                    </div>
                    <div className="text-sm font-medium text-[#131B2F]">
                        {filter === "completed" ? "No completed to-do items yet." : "No outstanding to-do items."}
                    </div>
                    <div className="mt-1 text-xs text-[#4B5565]">
                        {filter === "completed" ? "Items you complete will appear here." : "Click New to-do to create one."}
                    </div>
                </div>
            ) : (
                <ul className="space-y-2" data-testid={`${testidPrefix}-list`}>
                    {rows.map((r) => {
                        const state = r.status === "Completed" ? "completed"
                                    : r.is_overdue ? "overdue"
                                    : "open";
                        const stateStyle = {
                            completed: "bg-[#DCFCE7] text-[#166534]",
                            overdue: "bg-[#FEE2E2] text-[#7C1D1D]",
                            open: "bg-[#EFF6FF] text-[#1E3A8A]",
                        }[state];
                        return (
                            <li
                                key={r.id}
                                data-testid={`${testidPrefix}-row-${r.id}`}
                                className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-4 hover:border-[#CBD5E1] transition-colors"
                            >
                                <div className="flex items-start gap-3">
                                    <div className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center ${stateStyle}`}>
                                        <CheckCircle2 size={18} strokeWidth={1.75} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className={`text-sm font-semibold text-[#131B2F] truncate ${r.status === "Completed" ? "line-through text-[#4B5565]" : ""}`}>
                                                {r.title}
                                            </div>
                                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${stateStyle}`}>
                                                {state === "completed" ? "Completed" : state === "overdue" ? "Overdue" : (r.due_date ? "Due" : "Open")}
                                            </span>
                                        </div>
                                        {r.description && (
                                            <div className="mt-1 text-xs text-[#4B5565] line-clamp-2">{r.description}</div>
                                        )}
                                        <div className="mt-2 flex items-center justify-between gap-3">
                                            <span className="text-[11px] text-[#4B5565]">
                                                {r.due_date ? `Due ${r.due_date}` : "No due date"}
                                                {r.project_customer_name ? ` · ${r.project_customer_name}` : ""}
                                            </span>
                                            {r.status !== "Completed" && (
                                                <button
                                                    type="button"
                                                    onClick={() => onComplete(r)}
                                                    disabled={completingId === r.id}
                                                    data-testid={`${testidPrefix}-complete-${r.id}`}
                                                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-[#3E9E2E] text-white text-xs font-semibold hover:bg-[#348426] disabled:opacity-60"
                                                >
                                                    {completingId === r.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                                                    Mark complete
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {additionalSection}

            <NewTodoModal
                open={modalOpen}
                projectOptions={projectOptions}
                onCancel={() => setModalOpen(false)}
                onCreated={onCreated}
            />
        </PageContainer>
    );
}
